// ABOUTME: Renders approval recipients and their action and e-mail delivery state.
// ABOUTME: Keeps queued, ambiguous, and failed deliveries visible to recruiters.
import React from "react";
import { Typography, Box } from "@material-ui/core";
import { useDialogStyles } from "../styles/dialogStyles";
import {
    getStatusColor,
    getStatusIcon,
    StageStatus
} from "../utils/statusUtils";
import { TFunction } from "i18next";
import { formatDate } from "functions/formatValues";

interface ApproversTimelineProps {
    stageStatus: StageStatus;
    jobStatus: CompleoShared.Common.JobStatus;
    approvers: Compleo.JobApproval.jobApprovalDetailsApproverNotification[];
    t: TFunction;
}

function getEmailStatusLabel(
    emailStatus: string | undefined,
    t: TFunction
): string {
    switch (emailStatus) {
        case "sent":
            return t("emailSent", { defaultValue: "Email sent" });
        case "delivered":
            return t("emailDelivered", { defaultValue: "Email delivered" });
        case "queued":
            return t("emailQueued", { defaultValue: "Queued" });
        case "sending":
            return t("emailSending", { defaultValue: "Sending" });
        case "uncertain":
            return t("emailUncertain", {
                defaultValue: "Delivery not confirmed"
            });
        case "error":
            return t("emailError", { defaultValue: "Email failed" });
        default:
            return t("pending");
    }
}

export function ApproversTimeline({
    approvers,
    t,
    stageStatus,
    jobStatus
}: ApproversTimelineProps) {
    const classes = useDialogStyles();

    const sortedApprovers = [...approvers].sort((a, b) => {
        const dateA = a?.actionResult?.actionDate || "";
        const dateB = b?.actionResult?.actionDate || "";
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return new Date(dateA).getTime() - new Date(dateB).getTime();
    });

    return (
        <div className={classes.timelineContainer}>
            {sortedApprovers.map((approver, index) => {
                let status = approver.actionResult?.status || "pending";
                const dateLimit = approver.dateLimit
                    ? new Date(approver.dateLimit)
                    : null;
                if (jobStatus !== "jobApprovalInProgress") {
                    if (status === "pending") {
                        status = "canceled";
                    }
                }
                if (
                    jobStatus === "jobApprovalExpired" &&
                    stageStatus !== "canceled"
                ) {
                    const isExpired = dateLimit && new Date() > dateLimit;
                    if (status === "pending" && isExpired) {
                        status = "expired";
                    }
                }
                const markerStatus = approver.actionResult
                    ? status
                    : status === "canceled" || status === "expired"
                      ? status
                      : approver.emailStatus || status;
                const isLast = index === sortedApprovers.length - 1;

                return (
                    <div key={index} style={{ position: "relative" }}>
                        <div
                            className={classes.timelineDot}
                            style={{
                                backgroundColor: getStatusColor(markerStatus)
                            }}
                        >
                            {getStatusIcon(markerStatus)}
                        </div>

                        {!isLast && (
                            <div className={classes.timelineConnector} />
                        )}

                        <div className={classes.approverCard}>
                            <Typography className={classes.approverName}>
                                {approver.approverName || t("externalApprover")}
                            </Typography>
                            <Typography className={classes.approverEmail}>
                                {approver.approverEmail}
                            </Typography>

                            {approver.actionResult ? (
                                <Box>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <span
                                            className={classes.actionStatus}
                                            style={{
                                                backgroundColor: getStatusColor(
                                                    status
                                                )
                                            }}
                                        >
                                            {t(
                                                approver.actionResult.status ||
                                                    status
                                            )}
                                        </span>
                                        <Typography
                                            className={classes.actionDate}
                                        >
                                            {formatDate(
                                                approver.actionResult
                                                    .actionDate,
                                                "pt-BR",
                                                "dayMonthYearTime"
                                            )}
                                        </Typography>
                                    </Box>

                                    {approver.actionResult.notes && (
                                        <div className={classes.notesContainer}>
                                            <Typography
                                                className={classes.notesText}
                                            >
                                                "{approver.actionResult.notes}"
                                            </Typography>
                                        </div>
                                    )}
                                </Box>
                            ) : (
                                <Box display="flex" alignItems="center">
                                    <span
                                        className={classes.actionStatus}
                                        style={{
                                            backgroundColor: getStatusColor(
                                                status
                                            )
                                        }}
                                    >
                                        {t(status)}
                                    </span>
                                    <Typography className={classes.actionDate}>
                                        {getEmailStatusLabel(
                                            approver.emailStatus,
                                            t
                                        )}
                                        {approver.emailSentAt ? ":" : ""}
                                    </Typography>
                                    {approver.emailSentAt && (
                                        <Typography
                                            className={classes.actionDate}
                                            style={{
                                                marginLeft: 8,
                                                fontWeight: 800
                                            }}
                                        >
                                            {formatDate(
                                                approver.emailSentAt,
                                                "pt-BR",
                                                "dayMonthYearTime"
                                            )}
                                        </Typography>
                                    )}
                                    {approver.dateLimit && (
                                        <>
                                            <Typography
                                                className={classes.actionDate}
                                                style={{ marginLeft: 16 }}
                                            >
                                                Data limite:
                                            </Typography>

                                            <Typography
                                                className={classes.actionDate}
                                                style={{
                                                    marginLeft: 8,
                                                    fontWeight: 800
                                                }}
                                            >
                                                {formatDate(
                                                    approver.dateLimit,
                                                    "pt-BR",
                                                    "dayMonthYearTime"
                                                )}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
